import type { GetStaticPaths, GetStaticProps, NextPage } from 'next'

import Container from '~/components/atoms/Container/Container'
import Fixture from '~/components/atoms/Fixture/Fixture'
import type { JobListingDetailsProps } from '~/components/blocks/JobListingDetails/JobListingDetails'
import JobListingDetails from '~/components/blocks/JobListingDetails/JobListingDetails'
import { myPortfolioBanner } from '~/schemas/common'
import { useSanity } from '~/services/sanity'
import { addTranslations } from '~/utils/i18n'
import { slugFromContext } from '~/utils/urlResolver'
import { withLocalizedSettings } from '~/utils/withLocalizedSettings'

export interface PageData {
  jobListingData: JobListingDetailsProps
}

const JobListing: NextPage<PageData> = ({ jobListingData }: PageData) => {
  return (
    <Fixture verticalPadding>
      <Container>
        <JobListingDetails {...jobListingData} />
      </Container>
    </Fixture>
  )
}

const getStaticPropsOriginal: GetStaticProps = async function getStaticProps(
  context
) {
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { client, limiter, scorer } = await useSanity(context)
    const lang = (context?.params?.lang as Locale) || 'en'
    const jobListingData = await client.fetch(
      `*[_type == 'jobListing' && (slug.current == $slug || $slug in _pastSlugs) ${limiter}] ${scorer}[0]{
          "slug": slug.current,
          title,
          department,
          location,
          contractType,
          summary,
          body,
          applicationUrl,
          ${myPortfolioBanner}
      }`,
      {
        slug: slugFromContext(context),
        lang,
      }
    )

    return {
      props: {
        url: `/jobs/${jobListingData.slug}`,
        jobListingData,
        hasGoogleReviews: false,
        fullGoogleReviews: true,
        meta: {
          title: `${jobListingData.title} | MyArtBroker`,
        },
      },
    }
  } catch {
    return { props: {} }
  }
}

// Add SSR translation support
export const getStaticProps = withLocalizedSettings(async context => {
  const result = await getStaticPropsOriginal(context)

  if ('props' in result) {
    return {
      props: await addTranslations(result.props, context),
    }
  }

  return result
})

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: [],
  fallback: 'blocking',
})

export default JobListing
